import {
  FeedbackOrigin,
  FeedbackMenu,
  FeedbackBadge,
  ReportAiDisclosure,
} from '../../components/ai/DisclosureControls';
import { getFeedbackPreference } from '../../components/ai/aiDisclosure';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Button,
  Form,
  Comment,
  Icon,
  Popup,
  Grid,
  Dropdown,
} from 'semantic-ui-react';
import moment from 'moment';
import { useResizeDetector } from 'react-resize-detector';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {useFeatureFlags} from '../../hooks/useFeatureFlags';
import FeedbackReportModal, {FeedbackReportTarget} from './feedbackReportModal';
import queryString from 'query-string';
import debounce from 'lodash/debounce';

import Avatar from '../../components/avatar/avatar';
import { getReaction } from '../../utils/reactionUtil';
import accessUtil from '../../utils/accessUtil';
import authUtil, { getFreshIdToken } from '../../utils/authUtil';

import {
  setActiveInlineComment,
  submitComment,
  setFilteredReaders,
  updateComment,
  editComment,
  editReply,
  updateReply,
  deleteReply,
  editCommentPlaceHolderText,
  removeInlineCommentPlaceholder,
  deleteComment,
  submitReply,
} from '../../modules/comment';

import TextAreaWithSave from '../../components/inputs/TextAreaWithSave';

const WordCount = styled.div<{ warning: boolean }>`
  font-size: 0.8em;
  font-style: italic;
  display: flex;
  width: 100%;
  justify-content: flex-end;
  ${({ theme, warning }) => css`
    color: ${warning ? theme.font.warning : theme.font.primary};
  `}
`;

const CommentTopline = styled.div`
  display: flex;
  flex-wrap: wrap-reverse;
  align-items: flex-end;
  gap: 6px 12px;
`;

const CommentFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 10px;
  margin-top: 8px;
  && > .ui.dropdown {
    margin-left: 0;
  }
  && > .actions {
    margin: 0;
  }
`;

const CommentHeader = styled.div<{ $compact: boolean }>`
  flex: 1 1 180px;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  ${({ $compact }) =>
    $compact &&
    css`
      flex-basis: 120px;
      > div {
        min-width: 0;
      }
      > div:first-child {
        flex-shrink: 0;
      }
      .author {
        white-space: normal !important;
        overflow-wrap: anywhere;
      }
    `}
`;

const CommentTextWithLineBreak = styled(Comment.Text)`
  margin-top: 5px;
  white-space: pre-line;
  hyphens: auto;
  word-break: break-word;
`;

const StyledComment = styled(Comment)`
  :hover {
    z-index: 999;
    box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
  }
`;

type Props = {
  comment: any;
  enableFilters?: boolean;
  isActive: boolean;
  hasReplies: boolean;
  showChapterLinks?: boolean;
  onSizeChange?: (commentId: string, height: number) => void;
  /** When true (reader sidebar): hide "Inline comment" label; icon focuses comment in text instead of linking to chapter */
  inReaderView?: boolean;
  /** Called when user clicks the focus icon in reader view – focus this comment and scroll to it in the text */
  onFocusInContent?: (comment: any) => void;
};

const CommentElement: React.FC<Props> = ({
  comment,
  enableFilters,
  isActive,
  hasReplies,
  showChapterLinks,
  onSizeChange,
  inReaderView = false,
  onFocusInContent,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const {flags} = useFeatureFlags();
  const { width, height, ref } = useResizeDetector({
    refreshMode: 'debounce',
    refreshRate: 150,
  });
  const compactFeedback = width !== undefined && width <= 420;

  const currentBook = useSelector(state => state.book.currentBook);
  const currentSpineIndex = useSelector(
    state => state.readerApp.currentSpineIndex
  );
  const commentStatusOptions = useSelector(
    state => state.comment.commentStatusOptions
  );
  const user = useSelector(state => state.user.user);
  const userProfile = useSelector(state => state.user.userProfile);
  const canReport = !!(flags?.moderation_reporting ||
    (flags?.moderation_reporting_beta_access_allowed && userProfile?.betaFeatures?.moderation_reporting)) &&
    user.uid === currentBook?.author?._id;
  const [reportTarget, setReportTarget] = useState<FeedbackReportTarget | null>(null);

  const [replyOrigin, setReplyOrigin] = useState(getFeedbackPreference());
  const [replyText, setReplyText] = useState('');
  const [newReplyText, setNewReplyText] = useState('');
  const [newComment, setNewComment] = useState(
    comment?._id?.substr(0, 11) === 'placeHolder'
  );
  const [aiOrigin, setAiOrigin] = useState(getFeedbackPreference());
  const [newCommentText, setNewCommentText] = useState(comment?.text);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /** Focus this comment if in url */
  useEffect(() => {
    const query = queryString.parse(location.search);
    if (comment._id === query.comment) {
      if (query.reply || query.crid) {
        // TODO: align these urls so that they use the same query parameter for comment and reply
        // a reply id is in the url, make sure that the replies are visible
        setShowReplies(true);
      }
      dispatch(
        setActiveInlineComment({ commentId: comment._id, show: 'sidebar' })
      );
    }
  }, []);

  /** Inform parent about size change */
  const debouncedOnSizeChange = useCallback(
    debounce((commentId: string, newHeight: number) => {
      onSizeChange && onSizeChange(commentId, newHeight);
    }, 100), // Adjust the debounce delay as needed
    []
  );
  useEffect(() => {
    if (height !== undefined) debouncedOnSizeChange(comment._id, height);
  }, [height]);

  const handleReplyTextUpdate = (event: any, data: any) => {
    setReplyText(data.value);
  };

  const handleNewCommentTextUpdate = (event: any, data: any) => {
    newComment && dispatch(editCommentPlaceHolderText(comment._id, data.value)); // only set comment text in redux on placeholder comment
    setNewCommentText(data.value);
  };

  const onSubmitReply = async () => {
    if (replyOrigin !== 'personal' || !(replyText?.length > 0)) {
      return;
    }
    const idToken = await authUtil.getFreshIdToken();
    dispatch(submitReply(idToken, comment, {text: replyText, aiOrigin: replyOrigin}, (saved: any) => {
      if (saved) {setShowReplyForm(false); setReplyText(''); setReplyOrigin('undeclared');}
    }));
  };

  const onSubmitComment = async (comment: any) => {
    setIsSubmitting(true);
    const idToken = await authUtil.getFreshIdToken();
    if (aiOrigin !== 'personal' || !(newCommentText?.length > 0) || !currentBook) {
      setIsSubmitting(false);
      return;
    }

    const currentChapter = currentBook.parts[currentSpineIndex];
    // if we have a chapter to comment on
    if (currentChapter !== undefined) {
      // submit the comment first
      dispatch(
        submitComment(
          idToken,
          currentBook._id,
          currentChapter._id,
          newCommentText,
          {
            aiOrigin,
            kind: 'inline',
            inlineInfo: comment.inlineInfo,
          },
          (commentId: any) => {
            setIsSubmitting(false);
            if (!commentId) return;
            setNewCommentText('');
            if (newComment) {
              dispatch(removeInlineCommentPlaceholder(comment._id));
              dispatch(setActiveInlineComment({ commentId: undefined, show: undefined }));
            }

            // make sure we blur the focus otherwise we loose the ability to show customToolBar
            if (document.activeElement instanceof HTMLElement && document.activeElement !== document.body) {
              document.activeElement.blur();
            }
          }
        )
      );
    } else { setIsSubmitting(false); }
  };

  const onDeleteComment = async (event: any, data: any) => {
    const idToken = await getFreshIdToken();
    dispatch(deleteComment(idToken, data['data-comment-id']));
  };

  const onDeleteReply = async (event: any, data: any) => {
    const idToken = await getFreshIdToken();
    dispatch(
      deleteReply(idToken, {
        bookId: comment.book._id,
        chapterId: comment.part._id,
        commentId: comment._id,
        replyId: data['data-reply-id'],
      })
    );
  };

  const toggleReplyForm = (event: any) => {
    preventDefaults(event);
    setShowReplyForm(!showReplyForm);
    setReplyOrigin(getFeedbackPreference());
    setReplyText('');
  };

  const toggleShowReplies = (event: any) => {
    preventDefaults(event);
    setShowReplies(!showReplies);
  };

  const handleCommentStatusUpdate = async (e: any, data: any) => {
    const idToken = await authUtil.getFreshIdToken();
    dispatch(
      updateComment(idToken, {
        bookId: comment.book._id,
        chapterId: comment.part._id,
        commentId: comment._id,
        status: data.value,
      })
    );
  };

  const handleCommentUpdate = async (comment: any) => {
    if (aiOrigin !== 'personal') return;
    const idToken = await authUtil.getFreshIdToken();
    dispatch(
      updateComment(idToken, {
        bookId: comment.book._id,
        chapterId: comment.part._id,
        commentId: comment._id,
        aiOrigin,
        text: newCommentText,
      }, (saved: any) => { if (saved) setNewCommentText(''); })
    );
  };

  const handleReplyUpdate = async (reply: any) => {
    if (replyOrigin !== 'personal') return;
    const idToken = await authUtil.getFreshIdToken();
    dispatch(
      updateReply(idToken, {
        bookId: comment.book._id,
        chapterId: comment.part._id,
        commentId: comment._id,
        replyId: reply._id,
        text: replyText,
        aiOrigin: replyOrigin,
      }, (saved: any) => {if (saved) {setReplyText(''); setShowReplyForm(false); setReplyOrigin('undeclared');}})
    );
  };

  const onEditCommentText = (e: any, data: any) => {
    dispatch(editComment({ commentId: comment._id, editing: true }));
    setAiOrigin('undeclared');
    setNewCommentText(comment.text);
  };

  const onCancelEditCommentText = () => {
    if (newComment) {
      dispatch(removeInlineCommentPlaceholder(comment._id));
    } else {
      dispatch(editComment({ commentId: comment._id, editing: false }));
    }
    setNewCommentText('');
    dispatch(setActiveInlineComment({ commentId: undefined, show: undefined }));
  };

  const onEditingReplyText = (e: any, data: any) => {
    const reply = comment.replies.find(
      (replyObject: any) => replyObject._id === data['data-reply-id']
    );
    if (reply) {
      dispatch(
        editReply({ commentId: comment._id, replyId: reply._id, editing: true })
      );
      setReplyText(reply.text);
      setReplyOrigin('undeclared');
      setShowReplyForm(false);
    }
  };

  const onCancelEditingReplyText = (e: any, data: any) => {
    if (newComment) {
      dispatch(removeInlineCommentPlaceholder());
    } else {
      dispatch(
        editReply({
          commentId: comment._id,
          replyId: data['data-reply-id'],
          editing: false,
        })
      );
    }
    setNewReplyText('');
  };

  const goToCommentInContent = (event: any) => {
    console.log('gotocomments incontnent');
    preventDefaults(event);
    if (!enableFilters) {
      return;
    }
    dispatch(setFilteredReaders(comment.user));
  };

  const preventDefaults = (event: any) => {
    event.stopPropagation();
    event.preventDefault();
  };

  if (comment.isLast && comment._id === undefined) {
    return null;
  }

  return (
    <div ref={ref}>
      <StyledComment
        className={isActive ? 'comment-element-active' : 'comment-element'}
      >
        <Comment.Content>
          <CommentTopline>
            <CommentHeader $compact={compactFeedback}>
              <div style={{ width: 25, height: 25 }}>
                <Avatar
                  name={comment.user?.displayName ?? t('Unnamed')}
                  userId={comment.user?._id ?? null}
                  diameter={25}
                />
              </div>
              <div
                style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
              >
                <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
                  <Comment.Author
                    onClick={goToCommentInContent}
                    as="span"
                    style={{ marginLeft: 5, whiteSpace: 'pre', flex: 1 }}
                  >
                    {comment.user?.displayName ?? t('Unnamed')}
                  </Comment.Author>
                  {((showChapterLinks &&
                    comment.part?._id &&
                    comment.contentVersion?.versionNumber !== undefined) ||
                    (inReaderView && onFocusInContent)) && (
                    <Comment.Metadata
                      key="title"
                      style={{ marginLeft: 'auto' }}
                    >
                      <span>
                        {!inReaderView &&
                          (comment.inlineInfo
                            ? t('InlineComment')
                            : t('ChapterComment'))}
                        {inReaderView && onFocusInContent ? (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              onFocusInContent(comment);
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onFocusInContent(comment);
                              }
                            }}
                            style={{
                              marginLeft: inReaderView ? 0 : 6,
                              cursor: 'pointer',
                              color: '#4183c4',
                              display: 'inline-block',
                              verticalAlign: 'middle',
                            }}
                            title={t('ShowInText')}
                            aria-label={t('ShowInText')}
                          >
                            <Icon name="linkify" style={{ color: '#4183c4' }} />
                          </span>
                        ) : (
                          <Link
                            to={`/books/${comment.book._id}/content/v${comment.contentVersion.versionNumber}/${comment.part._id}?cid=${comment._id}&kind=${comment.kind}`}
                            style={{
                              marginLeft: 6,
                              cursor: 'pointer',
                              color: '#4183c4',
                              display: 'inline-block',
                              verticalAlign: 'middle',
                            }}
                            title={t('GoToChapter')}
                            aria-label={t('GoToChapter')}
                          >
                            <Icon name="linkify" style={{ color: '#4183c4' }} />
                          </Link>
                        )}
                      </span>
                      {!inReaderView && comment.part?.title !== undefined && (
                        <>
                          <br />
                          <span>
                            {comment.part.title}
                            &nbsp; (v{comment.contentVersion.versionNumber})
                          </span>
                        </>
                      )}
                    </Comment.Metadata>
                  )}
                </div>
                <Comment.Metadata
                  style={{
                    fontWeight: 'normal',
                    fontSize: '0.9em',
                    color: 'var(--br-muted)',
                    marginLeft: 5,
                  }}
                >
                  {comment.contentUpdatedAt
                    ? moment(comment.contentUpdatedAt).format('llll')
                    : moment(comment.createdAt).format('llll')}
                </Comment.Metadata>
              </div>
              {comment.warning && (
                <Popup
                  trigger={
                    <Icon
                      style={{ marginLeft: 'auto', cursor: 'pointer' }}
                      name="warning sign"
                    />
                  }
                  content={t(comment.warning.key)}
                />
              )}
            </CommentHeader>
          </CommentTopline>
          {!comment.editing && (
            <CommentTextWithLineBreak>
              {/* {comment.reaction && getReaction(comment.reaction)} */}
              {!newComment && comment.text}
            </CommentTextWithLineBreak>
          )}
          <CommentFooter>
            {!newComment && (
              <FeedbackMenu
                origin={comment.aiOrigin}
                label="Comment actions"
                direction="right"
              >
                {user.uid === comment.user?._id && !comment.reaction && (
                  <Dropdown.Item
                    content={
                      <span style={{ fontSize: '0.8em' }}>{t('Edit')}</span>
                    }
                    data-comment-id={comment._id}
                    onClick={onEditCommentText}
                  />
                )}
                {user.uid === comment.user?._id && (
                  <Dropdown.Item
                    content={
                      <span style={{ fontSize: '0.8em' }}>{t('Delete')}</span>
                    }
                    data-comment-id={comment._id}
                    onClick={onDeleteComment}
                  />
                )}
                <ReportAiDisclosure
                  menuItem
                  bookId={currentBook._id}
                  feedbackId={comment._id}
                />
                {canReport && comment.user?._id && comment.user._id !== user.uid && (
                  <Dropdown.Item
                    text={t('ReportOrBlock')}
                    icon="flag outline"
                    onClick={() => setReportTarget({
                      kind: 'comment', contentId: comment._id,
                      userId: comment.user._id, displayName: comment.user.displayName || t('Unnamed')
                    })}
                  />
                )}
              </FeedbackMenu>
            )}
            <Comment.Actions style={{ cursor: 'pointer' }}>
              {comment.reaction && (
                <Comment.Action>{getReaction(comment.reaction)}</Comment.Action>
              )}
              {!showReplies &&
                !newComment &&
                !comment.editing &&
                accessUtil.isAllowed(currentBook.access, [
                  'edit',
                  'comment',
                ]) && (
                  <Comment.Action
                    data-comment-id={comment._id}
                    onClick={toggleReplyForm}
                  >
                    {t('EnterReply')}
                  </Comment.Action>
                )}
              {hasReplies && [
                !showReplies && (
                  <Comment.Action
                    key="show-replies-action"
                    data-comment-id={comment._id}
                    onClick={toggleShowReplies}
                  >
                    {t('ShowXReplies', { count: comment.replies.length })}
                  </Comment.Action>
                ),
                showReplies && (
                  <Comment.Action
                    key="hide-replies-action"
                    data-comment-id={comment._id}
                    onClick={toggleShowReplies}
                  >
                    {t('HideXReplies', { count: comment.replies.length })}
                  </Comment.Action>
                ),
              ]}
              {user.uid === currentBook.author._id && (
                <Comment.Action
                  style={{
                    float: 'right',
                    whiteSpace: 'nowrap',
                    display: 'bo',
                  }}
                >
                  <Dropdown
                    text={t(`Status${(comment.status || '').toUpperCase()}`)}
                    direction="left"
                    inline
                    options={commentStatusOptions}
                    defaultValue={comment.status}
                    onChange={handleCommentStatusUpdate}
                  />
                </Comment.Action>
              )}
            </Comment.Actions>
          </CommentFooter>
        </Comment.Content>
        {(newComment || comment.editing) && ( // form for inputing new comments
          <Form reply>
            <FeedbackOrigin value={aiOrigin} onChange={setAiOrigin} />
            <Form.Field
              control={TextAreaWithSave}
              data-comment-id={comment._id}
              onClick={(e: any) => {
                preventDefaults(e);
              }}
              value={comment.editing ? newCommentText : comment.text} // show the local state text only on update edits
              // value={newCommentText} // show the local state text only on update edits
              onChange={handleNewCommentTextUpdate}
              onSave={() => {
                if (newComment) {
                  onSubmitComment(comment);
                } else {
                  handleCommentUpdate(comment);
                }
              }}
              autoFocus
            />
            <Grid padded="horizontally" columns={2}>
              <WordCount warning={newCommentText?.length > 2000}>
                {Number(newCommentText?.length ?? 0).toLocaleString()} /{' '}
                {Number(2000).toLocaleString()}
              </WordCount>
              <Grid.Row>
                <Button
                  data-comment-id={comment._id}
                  disabled={
                    aiOrigin !== 'personal' ||
                    !newCommentText ||
                    newCommentText?.length === 0 ||
                    newCommentText?.length > 2000
                  }
                  onClick={e => {
                    preventDefaults(e);
                    if (newComment) {
                      onSubmitComment(comment);
                    } else {
                      handleCommentUpdate(comment);
                    }
                  }}
                  content={t('LeaveComment')}
                  loading={isSubmitting}
                  basic={
                    !newCommentText ||
                    (newCommentText &&
                      newCommentText.length === 0 &&
                      newComment) ||
                    (newCommentText &&
                      newCommentText.length > 0 &&
                      !newComment &&
                      newCommentText === comment.text)
                  }
                  primary
                  size="tiny"
                />
                <Button
                  data-comment-id={comment._id}
                  onClick={e => {
                    preventDefaults(e);
                    onCancelEditCommentText();
                  }}
                  content={t('Cancel')}
                  disabled={isSubmitting}
                  basic
                  size="tiny"
                  floated="right"
                />
              </Grid.Row>
            </Grid>
          </Form>
        )}
        {hasReplies && showReplies && (
          <Comment.Group style={{ padding: '1em 0 1em 0em' }}>
            {comment.replies.map((reply: any, index: any, array: any) => (
              <Comment key={reply._id}>
                <CommentTopline>
                  <CommentHeader $compact={compactFeedback}>
                    <div style={{ width: 22, height: 22 }}>
                      <Avatar
                        name={
                          reply.user.displayName &&
                          reply.user.displayName !== ''
                            ? reply.user.displayName
                            : t('Unnamed')
                        }
                        userId={reply.user._id}
                        diameter={22}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', flexDirection: 'row' }}>
                        <Comment.Author
                          as="span"
                          style={{ marginLeft: 5, whiteSpace: 'pre' }}
                        >
                          {reply.user.displayName &&
                          reply.user.displayName !== ''
                            ? reply.user.displayName
                            : t('Unnamed')}
                        </Comment.Author>
                      </div>
                      <Comment.Metadata
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.9em',
                          opacity: 0.5,
                          marginLeft: 5,
                        }}
                      >
                        {reply.updatedAt !== reply.createdAt
                          ? moment(reply.updatedAt).format('llll')
                          : moment(reply.createdAt).format('llll')}
                      </Comment.Metadata>
                    </div>
                    {reply.reaction && !reply.editing && (
                      <Comment.Metadata>
                        {getReaction(reply.reaction)}
                      </Comment.Metadata>
                    )}
                  </CommentHeader>
                </CommentTopline>
                <Comment.Content>
                  <CommentTextWithLineBreak>
                    {!reply.editing && reply.text}
                  </CommentTextWithLineBreak>
                  <CommentFooter>
                    {reply._id ? (
                      <FeedbackMenu
                        origin={reply.aiOrigin}
                        label="Reply actions"
                        direction="right"
                      >
                        {user.uid === reply.user._id && !reply.reaction && (
                          <Dropdown.Item
                            content={
                              <span style={{ fontSize: '0.8em' }}>
                                {t('Edit')}
                              </span>
                            }
                            data-reply-id={reply._id}
                            data-comment-id={comment._id}
                            onClick={onEditingReplyText}
                          />
                        )}
                        {user.uid === reply.user._id && (
                          <Dropdown.Item
                            content={
                              <span style={{ fontSize: '0.8em' }}>
                                {t('Delete')}
                              </span>
                            }
                            data-reply-id={reply._id}
                            data-comment-id={comment._id}
                            onClick={onDeleteReply}
                          />
                        )}
                        <ReportAiDisclosure
                          menuItem
                          bookId={currentBook._id}
                          feedbackId={reply._id}
                        />
                        {canReport && reply.user?._id && reply.user._id !== user.uid && (
                          <Dropdown.Item
                            text={t('ReportOrBlock')}
                            icon="flag outline"
                            onClick={() => setReportTarget({
                              kind: 'reply', contentId: reply._id,
                              userId: reply.user._id, displayName: reply.user.displayName || t('Unnamed')
                            })}
                          />
                        )}
                      </FeedbackMenu>
                    ) : (
                      <FeedbackBadge origin={reply.aiOrigin} />
                    )}
                    {index === array.length - 1 &&
                      !reply.editing &&
                      accessUtil.isAllowed(currentBook.access, [
                        'edit',
                        'comment',
                      ]) && (
                        <Comment.Actions>
                          <Comment.Action
                            data-comment-id={comment._id}
                            onClick={toggleReplyForm}
                          >
                            {t('EnterReply')}
                          </Comment.Action>
                        </Comment.Actions>
                      )}
                  </CommentFooter>
                </Comment.Content>
                {reply.editing && ( // inline replyform
                  <Form reply>
                    <FeedbackOrigin
                      value={replyOrigin}
                      onChange={setReplyOrigin}
                    />
                    <Form.TextArea
                      control={TextAreaWithSave}
                      onClick={(e: any) => {
                        preventDefaults(e);
                      }}
                      defaultValue={replyText}
                      data-comment-id={comment._id}
                      data-reply-id={reply._id}
                      onChange={handleReplyTextUpdate}
                      onSave={() => {
                        if (reply.editing) {
                          handleReplyUpdate(reply);
                        } else {
                          onSubmitReply();
                        }
                      }}
                    />
                    <Grid padded="horizontally" columns={2}>
                      <Grid.Row>
                        <Button
                          data-comment-id={comment._id}
                          data-reply-id={reply._id}
                          disabled={
                            replyOrigin !== 'personal' ||
                            !replyText ||
                            replyText.length === 0
                          }
                          onClick={e => {
                            preventDefaults(e);
                            if (reply.editing) {
                              handleReplyUpdate(reply);
                            } else {
                              onSubmitReply();
                            }
                          }}
                          loading={isSubmitting}
                          content={t('EnterReply')}
                          basic={Boolean(
                            !newReplyText ||
                            newReplyText.length === 0 ||
                            (newReplyText &&
                              newReplyText.length > 0 &&
                              !reply &&
                              reply.text === newReplyText)
                          )}
                          primary
                          size="tiny"
                        />
                        <Button
                          data-comment-id={comment._id}
                          data-reply-id={reply._id}
                          onClick={(e, data) => {
                            preventDefaults(e);
                            onCancelEditingReplyText(e, data);
                          }}
                          disabled={isSubmitting}
                          content={t('Cancel')}
                          basic
                          size="tiny"
                          // color='orange'
                          floated="right"
                        />
                      </Grid.Row>
                    </Grid>
                  </Form>
                )}
              </Comment>
            ))}
          </Comment.Group>
        )}
        {
          // form for adding new replies
          showReplyForm && !newComment && (
            <Form reply>
              <FeedbackOrigin value={replyOrigin} onChange={setReplyOrigin} />
              <Form.TextArea
                control={TextAreaWithSave}
                data-comment-id={comment._id}
                onClick={(e: any) => {
                  preventDefaults(e);
                }}
                value={replyText}
                onChange={handleReplyTextUpdate}
                onSave={() => {
                  onSubmitReply();
                }}
              />
              <Grid padded="horizontally" columns={2}>
                <Grid.Row>
                  <Button
                    data-comment-id={comment._id}
                    disabled={
                      replyOrigin !== 'personal' ||
                      !replyText ||
                      replyText.length === 0
                    }
                    onClick={e => {
                      preventDefaults(e);
                      onSubmitReply();
                    }}
                    content={t('EnterReply')}
                    loading={isSubmitting}
                    basic={
                      !newReplyText ||
                      newReplyText.length === 0
                    }
                    primary
                    size="tiny"
                  />
                  <Button
                    data-comment-id={comment._id}
                    onClick={(e, data) => {
                      preventDefaults(e);
                      toggleReplyForm(e);
                    }}
                    disabled={isSubmitting}
                    content={t('Cancel')}
                    basic
                    size="tiny"
                    floated="right"
                  />
                </Grid.Row>
              </Grid>
            </Form>
          )
        }
        {comment.copyOf && (
          <span style={{ opacity: 0.8, fontSize: '0.8em', color: 'grey' }}>
            <em>{t('CommentsCopiedFromOriginal')}</em>
          </span>
        )}
      </StyledComment>
      <FeedbackReportModal bookId={currentBook?._id} target={reportTarget} onClose={() => setReportTarget(null)} />
    </div>
  );
};
export default CommentElement;
